<script setup lang="ts">
/**
 * База знаний — макет 22:2: страница читается в колонке 940, дерево живёт в
 * сайдбаре, связанные задачи стоят чипами под текстом.
 *
 * Текст показывает тот же редактор, что и описание задачи, только read-only:
 * страница написана таким же markdown, и второй способ его отрисовать разошёлся
 * бы с первым на первом же нестандартном блоке.
 *
 * Обсуждения страницы из макета нет: комментариев у страницы в API не
 * существует (docs/design-gaps.md). История версий — DEV-1481.
 */
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { AppButton, AppSelect } from '@vinprop/design-system'
import { AlertTriangle, Book } from 'lucide-vue-next'
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { pathTo } from '@/features/knowledge/api'
import { useKnowledgeStore } from '@/features/knowledge/store'
import { formatStamp } from '@/features/issues/format'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useKnowledgeStore()

const pageId = computed(() => String(route.params.pageId ?? ''))

const spaceOptions = computed(() =>
  store.spaces.map((space) => ({ value: space.id, label: `${space.name} · ${space.key}` })),
)

/** Крошки страницы — путь по дереву; последняя не ссылка, это текущая. */
const crumbs = computed(() => {
  const path = store.page ? pathTo(store.pages, store.page.id) : []

  return [
    { label: t('nav.knowledge'), to: '/kb' },
    ...path.map((entry, index) => ({
      label: entry.title,
      to: index === path.length - 1 ? undefined : `/kb/${entry.id}`,
    })),
  ]
})

const meta = computed(() => {
  const page = store.page

  if (!page) return ''

  const when = formatStamp(page.updatedAt, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

  return [when && t('kb.updated', { when }), t('kb.version', { version: page.version })]
    .filter(Boolean)
    .join(' · ')
})

const onSpace = async (id: string) => {
  await store.selectSpace(id)
  // Пространство сменили — открытая страница осталась в чужом дереве.
  if (pageId.value) await router.push('/kb')
}

const open = async () => {
  await store.ensureSpaces()

  if (pageId.value) await store.openPage(pageId.value)
}

watch(pageId, (id) => {
  if (id) void store.openPage(id)
})

onMounted(open)
</script>

<template>
  <TrackerToolbar :breadcrumbs="crumbs">
    <AppSelect
      v-if="spaceOptions.length > 1"
      :model-value="store.spaceId"
      :options="spaceOptions"
      size="small"
      :aria-label="t('kb.space')"
      @update:model-value="onSpace(String($event))"
    />
  </TrackerToolbar>

  <div class="kb">
    <div v-if="store.isLoading && !store.page" class="kb__skeleton" />

    <TrackerEmptyState
      v-else-if="store.error"
      :icon="AlertTriangle"
      :title="t('kb.error.title')"
      :description="t('kb.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="open">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!store.spaces.length"
      :icon="Book"
      :title="t('kb.empty.title')"
      :description="t('kb.empty.description')"
    />

    <TrackerEmptyState
      v-else-if="!store.page"
      :icon="Book"
      :title="t('kb.pick.title')"
      :description="t('kb.pick.description')"
    />

    <article v-else class="kb__page">
      <h1 class="kb__title">{{ store.page.title }}</h1>
      <p class="kb__meta">{{ meta }}</p>

      <AppRichTextEditor
        :model-value="store.page.contentMd"
        format="markdown"
        readonly
        preset="issue"
      />

      <section v-if="store.links.length" class="kb__links">
        <h2 class="kb__overline">{{ t('kb.related') }}</h2>

        <div class="kb__chips">
          <RouterLink
            v-for="link in store.links"
            :key="link.id"
            class="kb__chip"
            :to="`/issue/${link.issueKey}`"
            :title="link.issueTitle"
          >
            {{ link.issueKey }}
          </RouterLink>
        </div>
      </section>
    </article>
  </div>
</template>

<style scoped>
.kb {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 40px 64px;
}

.kb__page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 940px;
}

.kb__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-semibold);
}

.kb__meta {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.kb__skeleton {
  height: 480px;
  max-width: 940px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.kb__links {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.kb__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.kb__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kb__chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.kb__chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
</style>
